// src/input/uiNav.ts — Gamepad + klavye ile DOM menü gezintisi (menü, duraklatma, kazanan paneli).
//
// Neden ayrı bir modül: maç içi girdi (inputManager + gamepad.ts) sim tick'ine bağlı, tahsissiz ve Btn maskesi üretir;
// menü ise DOM odağı, tekrar (repeat) zamanlaması ve "aktifleştir/geri" semantiği ister. İkisini aynı sınıfa sıkıştırmak
// sim yolunu kirletirdi. Burası rAF'ta çalışır, gerçek zamanı (ms) kullanır — sim değil, arayüz.
//
// Gezinti modeli SATIR/SÜTUN koordinatıdır (piksel geometrisi değil): menü panelleri farklı yüksekliklerde olduğu için
// piksel tabanlı en-yakın-komşu araması "karakter ızgarasından sağ panele geçiş"te yanlış hedef seçiyordu. Koordinatla
// hangi öğenin hangi yönde komşu olduğu tasarımcı tarafından açıkça yazılır.
//
// Öğe iki türdendir: (a) düğme — A/Enter ile `activate`; (b) eksen öğesi (`axis`) — sol/sağ DEĞERİ değiştirir (bot sayısı,
// zorluk, kalite), odak satırdan çıkmaz. Konsol menülerinin alışılmış davranışı budur.
//
// Odak halkası yalnız pad/klavye kullanılınca görünür (`revealed`): mouse ile gezen oyuncuya gereksiz halka gösterilmez.

/** Standart eşlemede gezinti için okuduğumuz düğmeler (DESIGN §2 tablosuyla aynı numaralar). */
const PAD_A = 0;
const PAD_B = 1;
const PAD_START = 9;
const PAD_DPAD_UP = 12;
const PAD_DPAD_DOWN = 13;
const PAD_DPAD_LEFT = 14;
const PAD_DPAD_RIGHT = 15;

/** Çubuk gezintide ölü bölge maç içindekinden (0.18) yüksek: menüde kazara komşuya atlamak istenmez. */
const NAV_DEADZONE = 0.5;
/** Basılı tutma: ilk tekrar gecikmesi ve tekrar aralığı (ms). Konsol arayüzü hissi. */
const REPEAT_DELAY_MS = 380;
const REPEAT_RATE_MS = 130;

/** Odak halkası stilini bir kez enjekte eder (index.html'e dokunmadan; menu/pause/winner aynı sınıfı kullanır). */
let styleInjected = false;
function injectStyle(): void {
  if (styleInjected || typeof document === 'undefined') return;
  styleInjected = true;
  const s = document.createElement('style');
  s.textContent = `
    .ui-focus { outline: 2px solid #ffd43b !important; outline-offset: 2px; box-shadow: 0 0 0 4px rgba(255,212,59,.22) !important; }
    .ui-focus-axis { position: relative; }
    .ui-focus-axis::after { content: '‹ ›'; position: absolute; right: -22px; top: 50%; transform: translateY(-50%); color: #ffd43b; font-size: 12px; opacity: .9; }
  `;
  document.head.appendChild(s);
}

/** Gezinti öğesi: DOM düğmesi ya da eksen (değer) satırı. */
export interface UiNavItem {
  /** Odak halkasının çizileceği öğe (düğme, satır kabı ya da select). */
  el: HTMLElement;
  /** Izgara koordinatı; yön tuşları bu koordinatlar üzerinde en yakın komşuyu seçer. */
  row: number;
  col: number;
  /** A / Enter. Eksen öğelerinde isteğe bağlı (ör. select'i açmak yerine değeri ilerletmek). */
  activate?: () => void;
  /** Sol/sağ değeri değiştirir (−1 / +1); tanımlıysa yatay hareket odağı taşımaz. */
  axis?: (dir: -1 | 1) => void;
  /** Odak bu öğeye GELDİĞİNDE (yalnız değişimde) çağrılır — karakter ızgarasında seçim ve önizleme odağı takip etsin. */
  onFocus?: () => void;
}

export interface UiNavOptions {
  /** B / Escape (geri, iptal). */
  onBack?: () => void;
  /** Start düğmesi (menüde: doğrudan maça başla). */
  onStart?: () => void;
  /** Pad bağlanınca/kopunca (arayüzde ipucu göstermek için). */
  onPadChange?: (connected: boolean) => void;
  /** Klavye oklarını da dinle (varsayılan açık; maç içinde kullanılmaz). */
  keyboard?: boolean;
  /** Escape'i de `onBack` sayar (varsayılan açık). Duraklatma kaplamasında KAPALI: Escape'i oyunun kendi duraklatma
   *  kancası zaten görüyor; ikisi birden çalışınca devam edip anında yeniden duraklıyordu. */
  keyboardBack?: boolean;
  /**
   * Arayüz sesi kancası: odak/değer değişiminde 'move', aktifleştirmede 'select', geri/iptalde 'back'.
   * Neden `SoundName` değil de dizge: girdi katmanı `view/audio`'yu bilmemeli (game → view → anim → sim tek yön);
   * sahibi (menu.ts / game.ts) kanca gövdesinde `audio.ui(kind)` çağırır.
   * 'select' AKTİFLEŞTİRMEDEN SONRA çağrılır: menüde DÖVÜŞ'e basınca sahibi kendi "başlat" sesini çalıp bu onayı
   * bastırabilsin (iki arayüz sesi üst üste binmesin).
   */
  onNavSound?: (kind: 'move' | 'select' | 'back') => void;
}

export type Dir = 'up' | 'down' | 'left' | 'right';

/**
 * Saf gezinti çekirdeği (DOM'suz, test edilir): koordinat ızgarasında `dir` yönündeki en yakın komşunun indeksi;
 * komşu yoksa `index` (kenarda dur — sarma yok, çünkü karakter ızgarasından sağ panele "sarılarak" geçmek şaşırtıcı).
 *
 * Puan: YAN EKSENDEKİ sapma baskındır (×10), ana eksendeki uzaklık ikincil (×1). Yani "sağa" basınca önce AYNI SATIRDAKİ
 * öğeler aranır, mesafe ne olursa olsun. Tersi (ilerlemeyi ağırlıklandırmak) uiNav.test'te yakalandı: son satırdaki
 * karakterden (satır 3, sütun 1) sağa basınca bir üst satırın bitişik öğesi (satır 2, sütun 2), sağ paneldeki aynı satır
 * öğesine (satır 3, sütun 4) tercih ediliyordu — kullanıcı "sağa" derken satırdan çıkmayı beklemez.
 */
export function navNext(items: readonly { row: number; col: number }[], index: number, dir: Dir): number {
  const cur = items[index];
  if (!cur) return index;
  const dRow = dir === 'up' ? -1 : dir === 'down' ? 1 : 0;
  const dCol = dir === 'left' ? -1 : dir === 'right' ? 1 : 0;
  let best = index;
  let bestScore = Infinity;
  for (let i = 0; i < items.length; i++) {
    if (i === index) continue;
    const it = items[i]!;
    const dr = it.row - cur.row;
    const dc = it.col - cur.col;
    const forward = dRow !== 0 ? dr * dRow : dc * dCol;
    if (forward <= 0) continue;
    const lateral = dRow !== 0 ? Math.abs(dc) : Math.abs(dr);
    const score = lateral * 10 + forward;
    if (score < bestScore) { bestScore = score; best = i; }
  }
  return best;
}

/**
 * Pad/klavye ile DOM gezintisi. Kullanım: `setItems(...)` → her rAF'ta `update(nowMs)` → çıkışta `dispose()`.
 * Tek pad (indeks 0) yeterli: menü tek oyuncu tarafından sürülür.
 */
export class UiNav {
  /** Son okumada pad bağlı mıydı. */
  padConnected = false;
  /** Odak halkası görünür mü (ilk pad/klavye girdisinden sonra). */
  revealed = false;

  private items: UiNavItem[] = [];
  private index = 0;
  private readonly opts: UiNavOptions;
  /** Yön başına: 0 = basılı değil, aksi hâlde bir sonraki tekrarın zamanı (ms). */
  private repeatAt: Record<Dir, number> = { up: 0, down: 0, left: 0, right: 0 };
  private held: Record<Dir, boolean> = { up: false, down: false, left: false, right: false };
  private prevA = false;
  private prevB = false;
  private prevStart = false;
  /**
   * İlk okuma "hazırlama" okumasıdır: o an ZATEN BASILI olan tuşlar taze basış sayılmaz.
   *
   * NEDEN: kaplamalar bir tuşa basılarak açılıyor ve yeni gezinti, tuş hâlâ parmağın altındayken kuruluyor.
   * `prevA` false başladığı için ilk kare o basışı YENİ sanıyordu. Somut belirti: pad'le ÇEVRİMİÇİ seçilince
   * lobi açılır açılmaz kapanıp menüye dönüyordu — çünkü "bağlanılıyor" fazında görünen tek düğme Vazgeç ve
   * odak oraya düşüyor. Pad kopup geri gelince de yeniden hazırlanır.
   */
  private primed = false;
  private disposed = false;

  /** Klavye dinleyicisi GERÇEKTEN kuruldu mu (kaldırırken aynı koşul kullanılsın; `injectStyle` gibi bu da
   *  DOM'suz ortamda sessiz kalır — testler UiNav'ı düz Node'da kuruyor). */
  private readonly keyboardBound: boolean;

  constructor(opts: UiNavOptions = {}) {
    this.opts = opts;
    injectStyle();
    this.keyboardBound = opts.keyboard !== false && typeof addEventListener === 'function';
    if (this.keyboardBound) addEventListener('keydown', this.onKey);
  }

  /** Gezinti öğelerini kurar; `focus` verilmezse ilk öğe seçilir (odak halkası ilk girdide görünür). */
  setItems(items: UiNavItem[], focus = 0): void {
    this.items = items;
    this.index = Math.max(0, Math.min(items.length - 1, focus));
    this.paint();
  }

  /** Odaklı öğe indeksi (menü yeniden kurulduğunda korumak için). */
  get focusIndex(): number { return this.index; }

  /** Odağı belirli bir öğeye taşır (ör. karakter onaylanınca DÖVÜŞ'e atla). `silent` ile onFocus çağrılmaz. */
  setFocus(index: number, silent = false): void {
    if (index < 0 || index >= this.items.length || index === this.index) return;
    this.index = index;
    this.paint();
    if (!silent) this.items[index]?.onFocus?.();
  }

  /** Her rAF: pad'i oku, tekrarları işle. `now` = performance.now(). */
  update(now: number): void {
    if (this.disposed) return;
    const pad = this.readPad();
    if (!!pad !== this.padConnected) {
      this.padConnected = !!pad;
      this.opts.onPadChange?.(this.padConnected);
    }
    if (!pad) {
      this.held.up = this.held.down = this.held.left = this.held.right = false;
      this.primed = false;                       // pad geri geldiğinde basılı tuş yeni basış sayılmasın
      return;
    }

    const b = pad.buttons;
    const down = (i: number): boolean => { const x = b[i]; return x !== undefined && (x.pressed || x.value > 0.5); };
    const ax = pad.axes[0] ?? 0;
    const ay = pad.axes[1] ?? 0;

    this.edge('up', down(PAD_DPAD_UP) || ay < -NAV_DEADZONE, now);
    this.edge('down', down(PAD_DPAD_DOWN) || ay > NAV_DEADZONE, now);
    this.edge('left', down(PAD_DPAD_LEFT) || ax < -NAV_DEADZONE, now);
    this.edge('right', down(PAD_DPAD_RIGHT) || ax > NAV_DEADZONE, now);

    // Hazırlama okuması: mevcut durumu kaydet, HİÇBİR eylem yapma (yukarıdaki `primed` notu).
    if (!this.primed) {
      this.primed = true;
      this.prevA = down(PAD_A); this.prevB = down(PAD_B); this.prevStart = down(PAD_START);
      return;
    }

    const a = down(PAD_A);
    if (a && !this.prevA) { this.reveal(); this.activate(); }
    this.prevA = a;

    const bb = down(PAD_B);
    if (bb && !this.prevB) { this.reveal(); this.back(); }
    this.prevB = bb;

    const st = down(PAD_START);
    if (st && !this.prevStart) { this.reveal(); this.opts.onStart?.(); }
    this.prevStart = st;
  }

  dispose(): void {
    this.disposed = true;
    if (this.keyboardBound) removeEventListener('keydown', this.onKey);
    for (const it of this.items) { it.el.classList.remove('ui-focus'); it.el.classList.remove('ui-focus-axis'); }
    this.items = [];
  }

  /* ------------------------------------------------------------------ iç */

  private readPad(): Gamepad | null {
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return null;
    const pads = navigator.getGamepads();
    for (let i = 0; i < pads.length; i++) { const p = pads[i]; if (p) return p; }
    return null;
  }

  /** Yön için basma kenarı + tekrar zamanlaması. */
  private edge(dir: Dir, active: boolean, now: number): void {
    if (!active) { this.held[dir] = false; this.repeatAt[dir] = 0; return; }
    if (!this.held[dir]) {
      this.held[dir] = true;
      this.repeatAt[dir] = now + REPEAT_DELAY_MS;
      this.move(dir);
      return;
    }
    if (now >= this.repeatAt[dir]) {
      this.repeatAt[dir] = now + REPEAT_RATE_MS;
      this.move(dir);
    }
  }

  private reveal(): void {
    if (this.revealed) return;
    this.revealed = true;
    this.paint();
  }

  /** Yön hareketi: eksen öğesinde sol/sağ değeri değiştirir, aksi hâlde koordinatta en yakın komşuya geçer.
   *  Ses YALNIZ gerçekten bir şey değiştiyse: kenarda komşu yokken tık atmak "duvara çarptım" bilgisini siler. */
  private move(dir: Dir): void {
    this.reveal();
    const cur = this.items[this.index];
    if (!cur) return;
    if (cur.axis && (dir === 'left' || dir === 'right')) { cur.axis(dir === 'left' ? -1 : 1); this.opts.onNavSound?.('move'); return; }

    const best = navNext(this.items, this.index, dir);
    if (best !== this.index) { this.index = best; this.paint(); this.items[best]?.onFocus?.(); this.opts.onNavSound?.('move'); }
  }

  private activate(): void {
    const it = this.items[this.index];
    if (!it) return;
    // Ses SONRA (bkz. UiNavOptions.onNavSound): aktifleştirme menüyü kapatabilir; sahibi o durumda kendi sesini çalar.
    if (it.activate) { it.activate(); this.opts.onNavSound?.('select'); return; }
    // Eksen öğesinde A değeri ilerletir (select'i açmak yerine — pad'de açılan select gezilemez) → onay değil, değer değişimi.
    if (it.axis) { it.axis(1); this.opts.onNavSound?.('move'); }
  }

  /** Geri / iptal (B ya da Escape): ses + kanca. Kanca YOKSA ses de yok — menüde B'ye basınca hiçbir şey olmuyor,
   *  "iptal ettim" sesi duymak yanıltıcı olurdu (hareket sesindeki kuralla aynı: yalnız gerçekten olan şey duyulur). */
  private back(): void {
    const onBack = this.opts.onBack;
    if (!onBack) return;
    this.opts.onNavSound?.('back');
    onBack();
  }

  /** Odak halkasını günceller ve odaklı öğeyi görünür alana kaydırır. */
  private paint(): void {
    for (let i = 0; i < this.items.length; i++) {
      const it = this.items[i]!;
      const on = this.revealed && i === this.index;
      it.el.classList.toggle('ui-focus', on);
      it.el.classList.toggle('ui-focus-axis', on && !!it.axis);
      if (on) it.el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  /** Klavye eşdeğeri: oklar + WASD gezinti, Enter/Space aktifleştir, Escape geri. Menüde pad'siz oyuncu için. */
  // (onKey aşağıda; kaplama yardımcısı sınıfın dışında.)
  private readonly onKey = (e: KeyboardEvent): void => {
    if (this.disposed || e.ctrlKey || e.altKey || e.metaKey) return;
    const t = e.target as HTMLElement | null;
    // Select açıkken oklar seçeneği değiştirir — araya girme.
    if (t && (t.tagName === 'SELECT' || t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
    const c = e.code;
    let dir: Dir | null = null;
    if (c === 'ArrowUp' || c === 'KeyW') dir = 'up';
    else if (c === 'ArrowDown' || c === 'KeyS') dir = 'down';
    else if (c === 'ArrowLeft' || c === 'KeyA') dir = 'left';
    else if (c === 'ArrowRight' || c === 'KeyD') dir = 'right';
    if (dir) { e.preventDefault(); this.move(dir); return; }
    if (c === 'Space') { e.preventDefault(); this.reveal(); this.activate(); return; }
    // Enter menüde "başlat" olarak da kullanılıyor (menu.ts kendi dinleyicisi); burada yalnız odaklı öğeyi çalıştırır.
    if (c === 'Enter' && this.revealed) { e.preventDefault(); this.activate(); return; }
    if (c === 'Escape' && this.opts.keyboardBack !== false) { this.back(); }
  };
}

/* ------------------------------------------------------- kaplama (overlay) yardımcısı */

/**
 * Bir kaplama panelindeki (duraklatma, kazanan) düğmeleri pad ile gezilebilir yapar.
 * Kaplama her açıldığında çağrılır, kapanınca dönen `stop()` çalıştırılır — kaplama gizliyken pad girdisi maça gitmeli.
 *
 * Yerleşim DOM sırasından DEĞİL, ekrandaki gerçek konumdan çıkarılır: "Devam / Menü" yan yana duruyor, dikey liste
 * varsaymak sağ-sol tuşlarını ölü bırakıyordu (kullanıcı bulgusu). Düğmeler dikey merkezlerine göre satırlara ayrılır,
 * satır içinde soldan sağa sıralanır. Tek satır ya da tek sütun çıkarsa koordinatlar KÖŞEGEN verilir (satır i, sütun i):
 * böylece hem sağ-sol hem yukarı-aşağı aynı sırayı gezer — birkaç düğmelik panelde oyuncuyu doğru eksene zorlamak anlamsız.
 *
 * Neden ayrı: bu paneller maç döngüsünün içinde açılır (rAF zaten dönüyor), kendi rAF'ını kurmak yerine sahibi her
 * karede `tick(now)` çağırır. Sahibi yoksa (ör. maç durdu) `autoTick` ile kendi rAF'ını kurar.
 */
export function attachOverlayNav(
  root: HTMLElement,
  opts: { onBack?: () => void; autoTick?: boolean; keyboard?: boolean; keyboardBack?: boolean; onNavSound?: UiNavOptions['onNavSound'] } = {},
): { nav: UiNav; tick(now: number): void; stop(): void } {
  // GÖRÜNÜR ve ETKİN olanlar: gizli/pasif düğmeler ızgaraya girerse pad odağı görünmeyen bir hücreye
  // gidiyor ve A onu tıklıyor (lobinin faz ekranlarında yaşandı). `offsetParent === null` display:none'ı
  // yakalar; position:fixed öğeler için `getClientRects` yedeği var.
  const buttons = ([...root.querySelectorAll('button')] as HTMLButtonElement[])
    .filter((b) => !b.disabled && (b.offsetParent !== null || b.getClientRects().length > 0));
  const nav = new UiNav({ onBack: opts.onBack, keyboard: opts.keyboard, keyboardBack: opts.keyboardBack, onNavSound: opts.onNavSound });
  nav.setItems(layoutItems(buttons), 0);
  let raf = 0;
  let running = true;
  const tick = (now: number): void => { if (running) nav.update(now); };
  if (opts.autoTick !== false) {
    const loop = (now: number): void => { if (!running) return; raf = requestAnimationFrame(loop); nav.update(now); };
    raf = requestAnimationFrame(loop);
  }
  return {
    nav,
    tick,
    stop(): void { running = false; if (raf) cancelAnimationFrame(raf); nav.dispose(); },
  };
}

/**
 * Düğmelerin ekran konumundan gezinti koordinatı üretir (saf çekirdeği `gridCoords`; burada yalnız ölçüm).
 * Ölçüm alınamıyorsa (görünmez panel, test ortamı) DOM sırası dikey liste sayılır.
 */
function layoutItems(buttons: readonly HTMLButtonElement[]): UiNavItem[] {
  const boxes = buttons.map((b) => {
    const r = b.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2, h: r.height };
  });
  const coords = gridCoords(boxes);
  return buttons.map((b, i) => ({ el: b, row: coords[i]!.row, col: coords[i]!.col, activate: () => b.click() }));
}

/**
 * Kutu merkezlerinden satır/sütun koordinatı (DOM'suz, test edilir).
 * Aynı satır = dikey merkezleri düğme yüksekliğinin yarısından yakın olanlar. Tek satır ya da tek sütun çıkarsa
 * köşegen koordinat verilir (her iki eksende de gezilebilsin). Ölçüm yoksa (hepsi 0) DOM sırası dikey liste olur.
 */
export function gridCoords(boxes: readonly { x: number; y: number; h: number }[]): { row: number; col: number }[] {
  const n = boxes.length;
  const out: { row: number; col: number }[] = [];
  if (n === 0) return out;
  const measured = boxes.some((b) => b.h > 0 || b.x !== 0 || b.y !== 0);
  if (!measured) { for (let i = 0; i < n; i++) out.push({ row: i, col: i }); return out; }

  // Satırlara ayır: dikey merkezleri birbirine yakın olanlar aynı satır.
  const order = boxes.map((b, i) => ({ b, i })).sort((p, q) => p.b.y - q.b.y || p.b.x - q.b.x);
  const rows: { i: number; x: number }[][] = [];
  let curY = Number.NEGATIVE_INFINITY;
  let tol = 0;
  for (const { b, i } of order) {
    if (rows.length === 0 || Math.abs(b.y - curY) > tol) { rows.push([]); curY = b.y; tol = Math.max(b.h * 0.5, 1); }
    rows[rows.length - 1]!.push({ i, x: b.x });
  }
  for (const r of rows) r.sort((p, q) => p.x - q.x);

  const singleRow = rows.length === 1;
  const singleCol = rows.every((r) => r.length === 1);
  for (let ri = 0; ri < rows.length; ri++) {
    const r = rows[ri]!;
    for (let ci = 0; ci < r.length; ci++) {
      const idx = r[ci]!.i;
      // Tek satır: (0,ci) yerine (ci,ci); tek sütun: (ri,0) yerine (ri,ri) → iki eksen de aynı sırayı gezer.
      out[idx] = singleRow ? { row: ci, col: ci } : singleCol ? { row: ri, col: ri } : { row: ri, col: ci };
    }
  }
  return out;
}
